import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { IEditComplexModalState } from './interfaces';
import { deleteRequest, getRequest, postRequest, putRequest } from 'api/requests';
import {
	IAmenity,
	IConsultation,
	IFavourite,
	ILocation,
	IComplexPhoto,
	IPart,
} from 'interfaces/main';
import { fieldName } from 'consts/hardcode';
import { defaultComplex } from 'consts/default';

// Открытие окна с существующим ЖК
export const openEditComplexModal = createAction(
	'editComplexModal/openEditComplexModal',
	(complexId: number) => ({ payload: complexId }),
);

// Закрытие окна редактирования ЖК
export const closeModal = createAction('editComplexModal/closeModal');

// Загрузка всем ЖК по ID
export const getComplexById = createAsyncThunk(
	'editComplexModal/getComplexById',
	async ({ id }: { id: number }, { rejectWithValue }) => {
		try {
			const response = await getRequest(`complex/${id}`, {}, true);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Загрузка фотографий
export const uploadPhotos = createAsyncThunk(
	'editComplexModal/uploadPhotos',
	async ({ files, complexId }: { files: File[]; complexId: number }, { rejectWithValue }) => {
		try {
			// 👇 Create new FormData object and append files
			const data = new FormData();

			files.forEach((file, i) => {
				data.append(`file-${i}`, file, file.name);
			});

			const response = await postRequest(`complexPhoto/upload?complexId=${complexId}`, data);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Загрузка обложки
export const uploadCover = createAsyncThunk(
	'editComplexModal/uploadCover',
	async ({ file, complexId }: { file: File; complexId: number }, { rejectWithValue }) => {
		try {
			// 👇 Create new FormData object and append files
			const data = new FormData();
			data.append(`cover`, file, file.name);

			const response = await postRequest(`complexCover/upload?complexId=${complexId}`, data);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление фотографии
export const removePhoto = createAsyncThunk(
	'editComplexModal/removePhoto',
	async ({ id }: { id: number }, { rejectWithValue }) => {
		try {
			const response = await deleteRequest(`complexPhoto/${id}`);

			return response.data.id;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление обложки
export const removeCover = createAsyncThunk(
	'editComplexModal/removeCover',
	async (id: number, { rejectWithValue }) => {
		try {
			return await deleteRequest(`complexCover/${id}`);
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Установка значения в поле организации
export const changeComplex = createAsyncThunk(
	'editComplexModal/changeComplex',
	async (
		{
			key,
			complexId,
			value,
		}: {
			key: string;
			complexId: number;
			value: string | number | boolean;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await putRequest(
				`complex/${complexId}`,
				{
					[key]: value,
				},
				true,
			);

			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Добавление нового значения в мультиполе
export const addMultiSelect = createAsyncThunk(
	'editComplexModal/addMultiSelect',
	async (
		{
			objectId,
			complexId,
			key,
		}: {
			objectId: number;
			complexId: number;
			key: 'location' | 'amenity' | 'favourite' | 'consultation';
		},
		{ rejectWithValue, getState },
	) => {
		try {
			const field = fieldName[key];
			const { libraries } = getState() as RootState;
			const list = libraries[field.multiple];
			const newItem = list.find(
				(item: ILocation | IAmenity | IFavourite | IConsultation) => item.id === objectId,
			);

			const response = await postRequest(
				`complex${field.capitalLetter}`,
				{
					complexId,
					[field.foreignKey]: objectId,
				},
				true,
			);

			return {
				fieldName: field.multiple,
				data: { ...newItem, [`complex_${field.single}`]: response.data },
			};
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление значения в мультиполе
export const removeMultiSelect = createAsyncThunk(
	'editComplexModal/removeMultiSelect',
	async (
		{
			objectId,
			complexId,
			key,
		}: {
			objectId: number;
			complexId: number;
			key: 'location' | 'amenity' | 'favourite' | 'consultation';
		},
		{ rejectWithValue },
	) => {
		try {
			const field = fieldName[key];

			const response = await deleteRequest(
				`complex${field.capitalLetter}`,
				{
					complexId,
					[field.foreignKey]: objectId,
				},
				true,
			);

			return { fieldName: field.multiple, id: response.data.id };
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Добавление элемента массива
export const addArrayItem = createAsyncThunk(
	'editOrganizationModal/addArrayItem',
	async ({ arrayName, item }: { arrayName: string; item: IPart }, { rejectWithValue }) => {
		try {
			const response = await postRequest(arrayName, item, true);

			return { arrayName, data: response.data };
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Изменение элемента массива
export const changeArrayItem = createAsyncThunk(
	'editOrganizationModal/changeArrayItem',
	async (
		{
			arrayName,
			key,
			itemId,
			value,
		}: {
			arrayName: 'part';
			key: string;
			itemId: number;
			value: string | number | boolean;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await putRequest(
				`${arrayName}/${itemId}`,
				{
					[key]: value,
				},
				true,
			);

			return { arrayName, data: response.data };
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Удаление части оплаты
export const removeArrayItem = createAsyncThunk(
	'editOrganizationModal/removeArrayItem',
	async (
		{
			arrayName,
			itemId,
		}: {
			arrayName: 'part';
			itemId: number;
		},
		{ rejectWithValue },
	) => {
		try {
			const response = await deleteRequest(`${arrayName}/${itemId}`, {}, true);

			return { arrayName, data: response.data };
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

const initialState: IEditComplexModalState = {
	data: defaultComplex,
	isLoading: false,
	isPhotosLoading: false,
	isCoverLoading: false,
	isVisible: false,
};

const editComplexModalSlice = createSlice({
	name: 'editComplexModal',
	initialState,
	reducers: {
		closeModal: () => initialState,
		openEditComplexModal: (_, { payload }) => ({
			...initialState,
			isVisible: true,
			data: {
				...defaultComplex,
				id: payload,
			},
		}),
	},
	extraReducers: (builder) => {
		builder
			.addCase(getComplexById.pending, (state) => ({
				...state,
				isLoading: true,
			}))
			.addCase(getComplexById.rejected, (state) => ({
				...state,
				isLoading: false,
			}))
			.addCase(getComplexById.fulfilled, (state, { payload }) => ({
				...state,
				isLoading: false,
				data: {
					...state.data,
					...payload,
				},
			}))
			.addCase(changeComplex.fulfilled, (state, { payload }) => ({
				...state,
				data: {
					...state.data,
					...payload,
				},
			}))
			.addCase(addMultiSelect.fulfilled, (state, { payload }) => ({
				...state,
				data: {
					...state.data,
					[payload.fieldName]: [...state.data?.[payload.fieldName], payload.data],
				},
			}))
			.addCase(removeMultiSelect.fulfilled, (state, { payload }) => ({
				...state,
				data: {
					...state.data,
					[payload.fieldName]: state.data?.[payload.fieldName].filter(
						(item: ILocation | IAmenity | IFavourite | IConsultation) => item.id !== payload.id,
					),
				},
			}))
			.addCase(uploadPhotos.pending, (state) => ({
				...state,
				isPhotosLoading: true,
			}))
			.addCase(uploadPhotos.rejected, (state) => ({
				...state,
				isPhotosLoading: false,
			}))
			.addCase(uploadPhotos.fulfilled, (state, { payload }) => ({
				...state,
				isPhotosLoading: false,
				data: {
					...state.data,
					complexPhotos: [...state.data?.complexPhotos, ...payload],
				},
			}))
			.addCase(uploadCover.pending, (state) => ({
				...state,
				isCoverLoading: true,
			}))
			.addCase(uploadCover.rejected, (state) => ({
				...state,
				isCoverLoading: false,
			}))
			.addCase(uploadCover.fulfilled, (state, { payload }) => ({
				...state,
				isCoverLoading: false,
				data: {
					...state.data,
					complexCover: payload,
				},
			}))
			.addCase(removePhoto.pending, (state) => ({
				...state,
				isPhotosLoading: true,
			}))
			.addCase(removePhoto.rejected, (state) => ({
				...state,
				isPhotosLoading: false,
			}))
			.addCase(removePhoto.fulfilled, (state, { payload }) => ({
				...state,
				isPhotosLoading: false,
				data: {
					...state.data,
					complexPhotos: state.data.complexPhotos.filter(
						(item: IComplexPhoto) => item.id !== payload,
					),
				},
			}))
			.addCase(removeCover.pending, (state) => ({
				...state,
				isCoverLoading: true,
			}))
			.addCase(removeCover.rejected, (state) => ({
				...state,
				isCoverLoading: false,
			}))
			.addCase(removeCover.fulfilled, (state, { payload }) => ({
				...state,
				isCoverLoading: false,
				data: {
					...state.data,
					complexCover: null,
				},
			}))
			.addCase(addArrayItem.fulfilled, (state, { payload }) => {
				const field = fieldName[payload.arrayName].multiple;
				return {
					...state,
					data: {
						...state.data,
						[field]: [...state.data?.[field], payload.data],
					},
				};
			})
			.addCase(changeArrayItem.fulfilled, (state, { payload }) => {
				const field = fieldName[payload.arrayName].multiple;
				return {
					...state,
					data: {
						...state.data,
						[field]: state?.data?.[field].map((item: IPart) =>
							item.id === payload.data.id ? { ...item, ...payload.data } : item,
						),
					},
				};
			})
			.addCase(removeArrayItem.fulfilled, (state, { payload }) => {
				const field = fieldName[payload.arrayName].multiple;
				return {
					...state,
					data: {
						...state.data,
						[field]: state.data?.[field].filter((item: IPart) => item.id !== payload.data.id),
					},
				};
			});
	},
});

// Селекторы
export const EditComplexModalSelector = (state: RootState): IEditComplexModalState =>
	state.editComplexModal;

export const EditComplexModalLoadingSelector = createSelector(
	EditComplexModalSelector,
	({ isLoading }) => isLoading,
);

export const EditComplexModalPhotosLoadingSelector = createSelector(
	EditComplexModalSelector,
	({ isPhotosLoading }) => isPhotosLoading,
);

export const EditComplexModalCoverLoadingSelector = createSelector(
	EditComplexModalSelector,
	({ isCoverLoading }) => isCoverLoading,
);

export const EditComplexModalVisibleSelector = createSelector(
	EditComplexModalSelector,
	({ isVisible }) => isVisible,
);

export const EditComplexModalDataSelector = createSelector(
	EditComplexModalSelector,
	({ data }) => data,
);

export default editComplexModalSlice.reducer;
